import { Selection, BaseType } from "d3-selection";
import { HierarchyPointNode } from "d3-hierarchy";

import { IItem, IItemId, ItemType, ICrossContributorLink, TreeViewMode } from "./models";
import { isIEBrowser } from "@gtmhub/shared/utils";

export interface ICoordinates {
  x: number;
  y: number;
}

// Create shortcuts for d3 types as they can be very long
export type MySelection<Datum> = Selection<BaseType, Datum, BaseType, unknown>;
export type MyHierarchyPointNodeSelection<Datum> = MySelection<MyHierarchyPointNode<Datum>>;
export interface MyHierarchyPointNode<N> extends HierarchyPointNode<N> {
  title?: IMyHierarchyPointNodeTitle;
}
export interface IMyHierarchyPointNodeTitle {
  wrap?: string[];
  overflow?: boolean;
  lines?: number;
}

export const percentageText = (n: number): string => `${Math.round(n * 100)}%`;

export const cssClass = (obj: { [key: string]: boolean }): string =>
  Object.keys(obj)
    .filter((x) => obj[x])
    .join(" ");

export type WrapTitleConstants = {
  lineLength: number;
  maxLines: number;
  fontSize: number;
  fontWeight: number;
  lineHeight: number;
  titleWidth: string;
};

export const wrapTitle = (title: string, constants: WrapTitleConstants): IMyHierarchyPointNodeTitle => {
  const { lineLength, maxLines, fontSize, fontWeight, lineHeight, titleWidth } = constants;

  const regex = new RegExp(`.{1,${lineLength}}`, "g");
  let wrap = title.match(regex) || [];
  let overflow = false;
  let lines = wrap.length;

  // Keep both "if" because of IE11
  if (wrap.length > maxLines) {
    overflow = true;
    wrap = wrap.slice(0, maxLines);
    lines = maxLines;
  }

  if (title.length > maxLines * lineLength) {
    wrap[maxLines - 1] = wrap[maxLines - 1].slice(0, lineLength) + "...";
  }

  const isIE = isIEBrowser();

  // modern browsers
  if (!isIE) {
    const el = document.createElement("div");
    el.innerText = title;
    el.style.width = titleWidth;
    // .goal-title-text styles
    el.style.fontSize = `${fontSize}px`;
    el.style.lineHeight = `${lineHeight}px`;
    el.style.fontWeight = `${fontWeight}`;
    el.style.hyphens = "auto";

    document.body.appendChild(el);
    const strHeight = window.getComputedStyle(el).height;
    const height = +strHeight.slice(0, strHeight.length - 2); // remove "px"

    overflow = false;
    lines = height / lineHeight;
    if (lines > maxLines) {
      overflow = true;
      lines = maxLines;
    }

    el.remove();
  }

  return { wrap, overflow, lines };
};

export const itemEquals = (a: IItemId, b: IItemId): boolean => a.type === b.type && a.id === b.id;

export const nodeEquals = (a: { id: string; type: NodeType }, b: { id: string; type: NodeType }): boolean => a.type === b.type && a.id === b.id;

export type RootNodeType = "root";

export interface IRootNode {
  id: "root";
  type: RootNodeType;
  children: IItem[];
}

export const createRootDataNode = (dataNodes: IItem[]): IRootNode => ({
  id: "root",
  type: "root",
  children: dataNodes,
});

export type ShowMoreNodeType = "show-more";

export interface IShowMoreNode {
  id: string;
  type: ShowMoreNodeType;
  title: string;
  obfuscated?: boolean;
  parent: {
    id: string;
    type: RootNodeType | ItemType;
  };
}

export type Node = IRootNode | IShowMoreNode | IItem;

export type NodeType = ItemType | ShowMoreNodeType | RootNodeType;

export interface IContributorSubnode {
  node: MyHierarchyPointNode<IItem>;
  contributor: IItem;
  previousAdditionalTitleLines: number;
}

export interface ICrossContributorLinkEnd {
  node: MyHierarchyPointNode<IItem>;
  contributor: IItem;
  link: ICrossContributorLink;
}

export interface IMergedCrossContributorLink {
  source: ICrossContributorLinkEnd;
  target: ICrossContributorLinkEnd;
}

export const childrenCount = (item: IItem): number => item.contributors.length + item.supporters.length;

export interface IRenderingConstants {
  progressLabelPositionY: { objective: number; contributor: number; obfcustateObjective };
  progressLabelOffsetPositionX: number;
  contentHeightOffset: number;
  rowHeight: number;
  columnWidth: number;
  ownerNameHeight: number;
  ownersNamesHeight: number;
  linkWidth: number;
  itemWidth: number;
  itemTitlePadding: number;
  xCompensationForHiddenFakeRoot: number;
  showContributors: boolean;
  showOnlyContributorsOfTypeGoal: boolean;
  xTitle: number;
  titleWidth: number;
  titleWrap: {
    lineLength: number;
    maxLines: number;
  };
  itemTitleStyles: TitleStyles;
  contributorTitleStyles: TitleStyles;
  contributorTitleMaxWordLength: number;
  itemAvatarWidth: number;
  contributorAvatarWidth: number;
  itemTitleMaxWordLength: number;
}

export interface TitleStyles {
  lineHeight: number;
  fontSize: number;
  fontWeight: number;
}

export function rendering(): Record<TreeViewMode, IRenderingConstants> {
  return {
    compact: {
      progressLabelPositionY: {
        objective: 25,
        contributor: 2,
        obfcustateObjective: 22,
      },
      progressLabelOffsetPositionX: 10,
      rowHeight: 45,
      contentHeightOffset: 5,
      columnWidth: 610,
      ownerNameHeight: 0,
      ownersNamesHeight: 16,
      linkWidth: 100,
      itemWidth: 410,
      itemTitlePadding: 15,
      xCompensationForHiddenFakeRoot: 360,
      showContributors: true,
      showOnlyContributorsOfTypeGoal: true,
      xTitle: 36,
      titleWidth: 190,
      titleWrap: {
        lineLength: 30,
        maxLines: 2,
      },
      itemTitleStyles: {
        lineHeight: 20,
        fontSize: 15,
        fontWeight: 500,
      },
      contributorTitleStyles: {
        lineHeight: 16,
        fontSize: 13,
        fontWeight: 400,
      },
      contributorTitleMaxWordLength: 30,
      itemTitleMaxWordLength: 28,
      itemAvatarWidth: 24,
      contributorAvatarWidth: 24,
    },
    medium: {
      progressLabelPositionY: {
        objective: 25,
        contributor: 2,
        obfcustateObjective: 22,
      },
      progressLabelOffsetPositionX: 10,
      rowHeight: 45,
      contentHeightOffset: 5,
      columnWidth: 610,
      ownerNameHeight: 20,
      ownersNamesHeight: 16,
      linkWidth: 150,
      itemWidth: 410,
      itemTitlePadding: 15,
      xCompensationForHiddenFakeRoot: 610,
      showContributors: true,
      showOnlyContributorsOfTypeGoal: true,
      xTitle: 36,
      titleWidth: 190,
      titleWrap: {
        lineLength: 30,
        maxLines: 2,
      },
      itemTitleStyles: {
        lineHeight: 20,
        fontSize: 15,
        fontWeight: 500,
      },
      contributorTitleStyles: {
        lineHeight: 16,
        fontSize: 13,
        fontWeight: 400,
      },
      contributorTitleMaxWordLength: 30,
      itemTitleMaxWordLength: 28,
      itemAvatarWidth: 24,
      contributorAvatarWidth: 24,
    },
    relaxed: {
      progressLabelPositionY: {
        objective: 25,
        contributor: 2,
        obfcustateObjective: 22,
      },
      progressLabelOffsetPositionX: 10,
      rowHeight: 45,
      contentHeightOffset: 5,
      columnWidth: 610,
      ownerNameHeight: 20,
      ownersNamesHeight: 16,
      linkWidth: 200,
      itemWidth: 410,
      itemTitlePadding: 15,
      xCompensationForHiddenFakeRoot: 610,
      showContributors: true,
      showOnlyContributorsOfTypeGoal: false,
      xTitle: 36,
      titleWidth: 190,
      titleWrap: {
        lineLength: 30,
        maxLines: 2,
      },
      itemTitleStyles: {
        lineHeight: 20,
        fontSize: 15,
        fontWeight: 500,
      },
      contributorTitleStyles: {
        lineHeight: 16,
        fontSize: 13,
        fontWeight: 400,
      },
      contributorTitleMaxWordLength: 30,
      itemTitleMaxWordLength: 28,
      itemAvatarWidth: 24,
      contributorAvatarWidth: 24,
    },
  };
}
